import { StorageKeys } from '../../types/keys.type';
import ddLogger from '../../logging/ddLogger';
import { SharedUtils } from '../../utils';
import { AdjustmentsAnalyticsEvents } from '../../types/trip.type';
import { AdjustmentErrors } from '../../types/errors.type';
import moment from 'moment';
import { AdjustmentPreviewRequest } from 'guest-data-types-ts-lib';
import { CancellationSummary, TripAdjustmentResponse } from 'guest-data-types-ts-lib/types';
import _ from 'lodash';

export enum Step {
    PAYMENT = 'PAYMENT',
    CANCELLATION = 'CANCELLATION',
    PREVIEW = 'PREVIEW',
}

class SegmentService {
    public trackEvent(title: string, data?: { [key: string]: any }) {
        try {
            if ((window as any).analytics) {
                const reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
                const confirmationCode = SharedUtils.getSessionStorageItem(StorageKeys.CONFIRMATION_CODE);
                const userType = SharedUtils.getSessionStorageItem(StorageKeys.USER_TYPE);

                const options = {
                    category: 'guest',
                    page_version: 'guest_portal',
                    reservation_id: reservationId,
                    confirmation_code: confirmationCode,
                    user_type: userType,
                    ...data,
                };
                (window as any).analytics.track(title, options);
            }
        } catch (e) {
            ddLogger.error('Segment trackEvent Error', { error: { kind: e.message } });
        }
    }

    public trackPageView() {
        try {
            if ((window as any).analytics) {
                const reservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
                (window as any).analytics.page('Trip Portal Viewed', { reservation_id: reservationId });
            }
        } catch (e) {
            ddLogger.error('Segment trackPageView Error', { error: { kind: e.message } });
        }
    }

    public trackAdjustmentIssue(
        adjustmentPreviewRequest: AdjustmentPreviewRequest,
        issue: AdjustmentErrors,
        extras?: { [key: string]: string }
    ) {
        try {
            let details: { [key: string]: any } = {
                billing_type: adjustmentPreviewRequest.name,
                billing_issue: issue,
            };

            if (adjustmentPreviewRequest.name === 'addNight') {
                const { data } = adjustmentPreviewRequest;
                const ISO_FORMAT = 'YYYY-MM-DD';
                details = {
                    ...details,
                    additional_night: moment(data.date).format(ISO_FORMAT),
                };
            }

            if (adjustmentPreviewRequest.name === 'adjustOccupancyAndPets') {
                const { data } = adjustmentPreviewRequest;
                details = {
                    ...details,
                    adults: data.adults,
                    children: data.children,
                    pets: data.pets,
                };
            }

            if (adjustmentPreviewRequest.name === 'updatePetAdjustment') {
                const { data } = adjustmentPreviewRequest;
                details = {
                    ...details,
                    pets: data.pets,
                };
            }

            if (adjustmentPreviewRequest.name === 'extendedStay' || adjustmentPreviewRequest.name === 'lateCheckout') {
                details = {
                    ...details,
                    ...extras,
                };
            }

            ddLogger.info('Trip Portal - Billing Issues', details);
            this.trackEvent(AdjustmentsAnalyticsEvents.ADJUSTMENT_BILLING_ERROR, details);
        } catch (e) {
            ddLogger.error('ERROR tracking adjustment issue', e);
        }
    }

    public trackAdjustmentSuccess(
        step: Step,
        adjustmentPreviewResponse: TripAdjustmentResponse | CancellationSummary,
        extras: {
            new_reservation_total?: number;
            discount_amount?: number;
            reservation_checkin?: string;
            reservation_checkout?: string;
            reservation_balance?: number;
            pets?: number;
        }
    ) {
        try {
            let details: { [key: string]: any } = {};

            if ('adjustment' in adjustmentPreviewResponse) {
                details = this.getDetailInformation(adjustmentPreviewResponse, extras);
            }

            switch (step) {
                case Step.PREVIEW:
                    ddLogger.info('Tracking: Trip Portal - Billing Details', details);
                    this.trackEvent(AdjustmentsAnalyticsEvents.ADJUSTMENT_BILLING_DETAILS, details);
                    break;
                case Step.PAYMENT:
                    ddLogger.info('Tracking: Trip Portal - Payment Complete', details);
                    this.trackEvent(AdjustmentsAnalyticsEvents.ADJUSTMENT_PAYMENT_COMPLETE, details);
                    break;
                case Step.CANCELLATION:
                    ddLogger.info('Tracking: Trip Portal - Cancellation Complete');
                    this.trackEvent(AdjustmentsAnalyticsEvents.ADJUSTMENT_CANCELLATION_COMPLETE);
                    break;
            }
        } catch (e) {
            ddLogger.error('ERROR tracking adjustment success', e);
        }
    }

    private getDetailInformation(
        adjustmentPreviewResponse: TripAdjustmentResponse,
        extras: {
            new_reservation_total?: number;
            discount_amount?: number;
            reservation_checkin?: string;
            reservation_checkout?: string;
            reservation_balance?: number;
            pets?: number;
        }
    ): { [key: string]: any } {
        let details: { [key: string]: any } = {};
        const { detail, delta, adjustment } = adjustmentPreviewResponse;

        details = {
            adjustment: adjustment.name,
            adjustment_rent: delta.rent?.toFixed(2),
            adjustment_fees: delta.fees?.toFixed(2),
            adjustment_tax: delta.taxes?.toFixed(2),
            previous_balance: detail!.previous_balance.toFixed(2),
            total_balance: detail!.total.toFixed(2),
        };

        if (adjustment.name === 'addNight') {
            const ISO_FORMAT = 'YYYY-MM-DD';
            const {
                data: { date, discount_percent },
            } = adjustment;
            details = {
                ...details,
                additional_night: moment(date).format(ISO_FORMAT),
                discount_percent: discount_percent ? discount_percent : '0',
                discount_amount: extras.discount_amount ? extras.discount_amount.toFixed(2) : '0',
                new_reservation_total: extras.new_reservation_total && extras.new_reservation_total.toFixed(2),
            };
        }
        if (adjustment.name === 'tripAddNight') {
            const ISO_FORMAT = 'YYYY-MM-DD';
            const {
                data: { last_night },
            } = adjustment;
            details = {
                ...details,
                additional_night: moment(last_night).format(ISO_FORMAT),
                discount_amount: extras.discount_amount ? extras.discount_amount.toFixed(2) : '0',
                new_reservation_total: extras.new_reservation_total && extras.new_reservation_total.toFixed(2),
            };
        }
        if (adjustment.name === 'adjustOccupancyAndPets') {
            const { adults, children, pets } = adjustment.data;
            details = {
                ...details,
                adults,
                children,
                pets,
            };
        }
        if (adjustment.name === 'updatePetAdjustment') {
            const { pets } = adjustment.data;
            details = {
                ...details,
                pets,
            };
        }
        if (adjustment.name === 'extendedStay') {
            const { additional_nights_count, first_night, last_night } = adjustment.data;
            details = {
                ...details,
                ...extras,
                adjustment_checkin: first_night,
                adjustment_chekout: moment(last_night).add(1, 'days').format('YYYY-MM-DD'),
                new_reservation_total: delta.total + _.toNumber(adjustmentPreviewResponse.original_finances?.total),
                adjustment_total: delta.total,
                additional_nights: additional_nights_count,
            };
        }

        return details;
    }
}

export default new SegmentService();
